import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

const devWarn = (...args) => { if (process.env.NODE_ENV !== "production") console.warn(...args); };

const scriptTag = (src, id, attrs = {}) => {
  if (typeof document === "undefined") return null;
  if (document.getElementById(id)) return document.getElementById(id);
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
};

const inlineScript = (id, code) => {
  if (typeof document === "undefined" || document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.type = "text/javascript";
  s.text = code;
  document.head.appendChild(s);
};

/**
 * Consent Mode v2 defaults. Installed before any Google tag so EU visitors are
 * measured cookielessly until they accept, then upgraded in place.
 */
const CONSENT_KEYS = ["ad_storage", "ad_user_data", "ad_personalization", "analytics_storage"];

const installConsentDefaults = (granted) => {
  inlineScript("fl-consent-default", `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('consent', 'default', {
      ad_storage: '${granted ? "granted" : "denied"}',
      ad_user_data: '${granted ? "granted" : "denied"}',
      ad_personalization: '${granted ? "granted" : "denied"}',
      analytics_storage: '${granted ? "granted" : "denied"}',
      functionality_storage: 'granted',
      security_storage: 'granted',
      wait_for_update: 500
    });
    gtag('set', 'url_passthrough', true);
    gtag('set', 'ads_data_redaction', ${granted ? "false" : "true"});
  `);
};

/** Called by the cookie banner the moment the visitor accepts or rejects. */
export const updateConsent = (value) => {
  const state = value === "accepted" ? "granted" : "denied";
  try {
    if (!window.gtag) return;
    window.gtag("consent", "update", Object.fromEntries(CONSENT_KEYS.map((k) => [k, state])));
    window.gtag("set", "ads_data_redaction", state !== "granted");
  } catch (err) { devWarn("[MarketingLoader] consent update failed", err); }
};

const loadGoogle = (gaId, gtmId, adsId) => {
  if (!gaId && !gtmId && !adsId) return;
  const primary = gaId || adsId;
  if (primary) scriptTag(`https://www.googletagmanager.com/gtag/js?id=${primary}`, "fl-gtag");
  inlineScript("fl-gtag-init", `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    ${gaId ? `gtag('config', '${gaId}', { anonymize_ip: true });` : ""}
    ${adsId ? `gtag('config', '${adsId}');` : ""}
  `);
  if (gtmId) {
    inlineScript("fl-gtm", `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `);
  }
};

const loadMetaPixel = (id, granted, preConsent) => {
  if (!id) return;
  // Meta's own consent API: the pixel loads on the first pageview and holds events
  // until consent is granted (or fires immediately when the admin allows it).
  inlineScript("fl-fbq", `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    ${granted || preConsent ? "" : "fbq('consent', 'revoke');"}
    fbq('init', '${id}'); fbq('track', 'PageView');
  `);
  flushMeta();
};

// Events fired before the pixel script is installed (e.g. a product page opened directly
// from an ad) are queued and replayed, otherwise Meta never sees ViewContent / Purchase.
const pendingFb = [];
const fb = (...args) => {
  if (window.fbq) {
    try { window.fbq(...args); } catch (err) { devWarn("[MarketingLoader] fbq failed", args[1], err); }
  } else if (pendingFb.length < 50) pendingFb.push(args);
};
const flushMeta = () => { while (window.fbq && pendingFb.length) fb(...pendingFb.shift()); };

/** Meta matches events to catalogue items through content_ids (= feed `id` = product id). */
const metaParams = (p) => {
  const items = (p.items || []).filter((i) => i.item_id);
  const out = { currency: String(p.currency || "EUR").toUpperCase(), value: Number(p.value || 0) };
  if (items.length) {
    out.content_type = "product";
    out.content_ids = items.map((i) => String(i.item_id));
    out.contents = items.map((i) => ({ id: String(i.item_id), quantity: Number(i.quantity || 1), item_price: Number(i.price || 0) }));
    out.num_items = items.reduce((n, i) => n + Number(i.quantity || 1), 0);
    if (items.length === 1 && items[0].item_name) out.content_name = String(items[0].item_name);
  }
  if (p.transaction_id) out.order_id = String(p.transaction_id);
  return out;
};

const tiktokParams = (p) => {
  const items = (p.items || []).filter((i) => i.item_id);
  const out = { currency: String(p.currency || "EUR").toUpperCase(), value: Number(p.value || 0),
                contents: items.map((i) => ({ content_id: String(i.item_id), content_type: "product",
                                              content_name: String(i.item_name || ""),
                                              quantity: Number(i.quantity || 1), price: Number(i.price || 0) })) };
  if (p.search_string) out.search_string = String(p.search_string);
  if (p.transaction_id) out.order_id = String(p.transaction_id);
  return out;
};

const snapParams = (p) => {
  const items = (p.items || []).filter((i) => i.item_id);
  return { currency: String(p.currency || "EUR").toUpperCase(), price: Number(p.value || 0),
           item_ids: items.map((i) => String(i.item_id)),
           number_items: items.reduce((n, i) => n + Number(i.quantity || 1), 0),
           ...(p.transaction_id ? { transaction_id: String(p.transaction_id) } : {}) };
};

/** Meta pixel consent, mirrored from the cookie banner. */
/** TikTok consent API: grant on Accept; revoke on Reject unless pre-consent is on. */
export const updateTikTokConsent = (value) => {
  try {
    if (!window.ttq) return;
    if (value === "accepted") window.ttq.grantConsent();
    else if ((window.__fleurinelleMarketing || {}).tiktok_pixel_pre_consent !== "on") window.ttq.revokeConsent();
  } catch (err) { devWarn("[MarketingLoader] ttq consent failed", err); }
};

export const updateMetaConsent = (value) => {
  try { if (window.fbq) window.fbq("consent", value === "accepted" ? "grant" : "revoke"); }
  catch (err) { devWarn("[MarketingLoader] fbq consent failed", err); }
};

/**
 * Emit a partner-signup event to Google Analytics 4, Google Ads (conversion) and
 * Meta Pixel simultaneously. Use `audience: "florist"` or `"driver"` so campaigns
 * can build separate remarketing lists / lookalike audiences.
 *
 * Google Ads conversion labels: reads `google_ads_conversion_id` (AW-XXXX) and the
 * matching `_florist_signup_label` / `_driver_signup_label` from a globally-cached
 * marketing config (populated by MarketingLoader on boot). If both are set, we send
 * `AW-XXXX/LABEL` so Ads counts it as a proper conversion. If not set, we fall back
 * to a plain event with `send_to: <audience>_signup` — still useful for GA4 audiences.
 *
 * Consent-safe: silently no-ops when a pixel is not loaded (revoked consent).
 */
export const trackPartnerSignup = ({ audience, applicationId, city, mode } = {}) => {
  const payload = {
    audience: audience || "partner",
    application_id: applicationId || "",
    city: city || "",
    delivery_mode: mode || "",
  };
  const cfg = (typeof window !== "undefined" && window.__fleurinelleMarketing) || {};
  const adsId = cfg.google_ads_conversion_id || "";
  const label = audience === "driver" ? (cfg.google_ads_driver_signup_label || "")
                                       : (cfg.google_ads_florist_signup_label || "");
  const sendTo = adsId && label ? `${adsId}/${label}` : `${audience || "partner"}_signup`;
  try {
    window.gtag?.("event", "partner_signup", payload);
    window.gtag?.("event", "conversion", { ...payload, send_to: sendTo });
  } catch (err) { devWarn("[MarketingLoader] gtag partner_signup failed", err); }
  try {
    fb("track", "CompleteRegistration", {
      content_name: `${payload.audience}_signup`,
      content_category: payload.audience,
      status: "submitted",
    });
  } catch (err) { devWarn("[MarketingLoader] fbq CompleteRegistration failed", err); }
  ttTrack("sign_up", { currency: "EUR", value: 0,
                       items: [{ item_id: `${payload.audience || "partner"}_signup`, item_name: `${payload.audience || "partner"} signup`, quantity: 1, price: 0 }] });
  try {
    window.dataLayer?.push({ event: "partner_signup", ...payload, send_to: sendTo });
  } catch (err) { devWarn("[MarketingLoader] dataLayer partner_signup failed", err); }
};

/** ChatGPT Ads (OpenAI) measurement pixel. Consent is set before init, per OpenAI's SDK. */
const loadOpenAiPixel = (pixelId, granted) => {
  if (!pixelId) return;
  inlineScript("fl-oaiq", `
    (function (w, d, s, u) {
      if (w.oaiq) return;
      var q = function () { q.q.push(arguments); };
      q.q = []; w.oaiq = q;
      var js = d.createElement(s); js.async = true; js.src = u;
      var f = d.getElementsByTagName(s)[0]; f.parentNode.insertBefore(js, f);
    })(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");
    ${granted ? "" : "oaiq('consent', false);"}
    oaiq("init", { pixelId: "${pixelId}" });
  `);
};

/** ChatGPT Ads consent, mirrored from the cookie banner. */
export const updateOpenAiConsent = (value) => {
  try { window.oaiq?.("consent", value === "accepted"); }
  catch (err) { devWarn("[MarketingLoader] oaiq consent failed", err); }
};

/** OpenAI amounts are integers in the currency's minor unit (8900 = 89.00). */
const ZERO_DECIMAL = ["JPY", "KRW", "VND", "CLP", "ISK", "XOF", "XAF", "KMF", "DJF", "GNF", "PYG", "RWF", "UGX", "VUV"];
export const minorUnits = (amount, currency) =>
  Math.round(Number(amount || 0) * (ZERO_DECIMAL.includes(String(currency || "").toUpperCase()) ? 1 : 100));

const OAI_EVENTS = { purchase: "order_created", add_to_cart: "items_added", begin_checkout: "checkout_started",
                     view_item: "contents_viewed", page_view: "page_viewed" };

const oaiMeasure = (name, params, eventId) => {
  const type = OAI_EVENTS[name];
  if (!type || !window.oaiq) return;
  const currency = String(params.currency || "MAD").toUpperCase();
  const data = { type: "contents" };
  if (params.value || params.value === 0) { data.amount = minorUnits(params.value, currency); data.currency = currency; }
  if (params.items?.length) {
    data.contents = params.items.map((i) => ({
      id: String(i.item_id || ""), name: String(i.item_name || ""), content_type: "product",
      quantity: Number(i.quantity || 1), amount: minorUnits(i.price, currency), currency,
    }));
  }
  try { window.oaiq("measure", type, data, eventId ? { event_id: eventId } : undefined); }
  catch (err) { devWarn("[MarketingLoader] oaiq failed", type, err); }
};

const metaTag = (name, content) => {
  if (typeof document === "undefined" || !content) return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

export const cookieValue = (name) => {
  try {
    return document.cookie.split("; ").find((c) => c.startsWith(`${name}=`))?.split("=").slice(1).join("=") || "";
  } catch (_) { return ""; }
};

// TikTok click id from ad landings (?ttclid=…), kept 7 days so later checkouts still match the ad.
const TTCLID_KEY = "fl_ttclid";
try {
  const fromUrl = new URLSearchParams(window.location.search).get("ttclid");
  if (fromUrl) localStorage.setItem(TTCLID_KEY, JSON.stringify({ v: fromUrl.slice(0, 500), at: Date.now() }));
} catch (_) {}

/** `_ttp` cookie + stored ttclid, sent with server events for TikTok matching. */
export const tiktokIds = () => {
  let ttclid = "";
  try {
    const saved = JSON.parse(localStorage.getItem(TTCLID_KEY) || "null");
    if (saved && Date.now() - saved.at < 7 * 86400000) ttclid = saved.v;
  } catch (_) {}
  return { ttp: cookieValue("_ttp"), ttclid };
};

const loadLinkedIn = (id) => {
  if (!id) return;
  inlineScript("fl-li", `
    _linkedin_partner_id = "${id}";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);
  `);
  scriptTag("https://snap.licdn.com/li.lms-analytics/insight.min.js", "fl-li-src");
};

const loadTikTok = (id) => {
  if (!id) return;
  inlineScript("fl-ttq", `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify",
      "instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie",
      "holdConsent","revokeConsent","grantConsent"],
      ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
      for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
      ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},
      ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};
      var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;
      var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
      ttq.load('${id}'); ttq.page();
    }(window, document, 'ttq');
  `);
  applyTikTokIdentity();
  flushTikTok();
};

/* ---------- TikTok: hashed advanced matching + standard events ---------- */
const TT_IDENT_KEY = "fl_tt_ident";
const sha256 = async (value) => {
  const v = String(value || "").trim();
  if (!v || !window.crypto?.subtle) return "";
  const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(v));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
};

const applyTikTokIdentity = () => {
  try {
    const ident = JSON.parse(localStorage.getItem(TT_IDENT_KEY) || "null");
    if (ident && window.ttq) window.ttq.identify(ident);
  } catch (err) { devWarn("[MarketingLoader] ttq identify failed", err); }
};

/** Hash (SHA-256, client side) and remember the buyer's email / phone / id for TikTok matching. */
export const identifyBuyer = async ({ email, phone, externalId } = {}) => {
  try {
    const prev = JSON.parse(localStorage.getItem(TT_IDENT_KEY) || "{}");
    const digits = String(phone || "").replace(/[^\d+]/g, "");
    const next = { ...prev };
    if (email) next.email = await sha256(String(email).toLowerCase());
    if (digits.length > 6) next.phone_number = await sha256(digits.startsWith("+") ? digits : `+${digits}`);
    if (externalId) next.external_id = await sha256(externalId);
    localStorage.setItem(TT_IDENT_KEY, JSON.stringify(next));
    applyTikTokIdentity();
  } catch (err) { devWarn("[MarketingLoader] identifyBuyer failed", err); }
};

const TT_EVENTS = { view_item: "ViewContent", add_to_cart: "AddToCart", begin_checkout: "InitiateCheckout",
                    purchase: "Purchase", place_order: "PlaceAnOrder", add_to_wishlist: "AddToWishlist",
                    search: "Search", sign_up: "CompleteRegistration", add_payment_info: "AddPaymentInfo" };
// Like Meta: events fired before the TikTok snippet is installed are queued and replayed on load.
const pendingTt = [];
const TT_CONTENT_EVENTS = ["view_item", "add_to_cart", "begin_checkout", "purchase", "place_order",
                           "add_to_wishlist", "add_payment_info", "search"];
const ttTrack = (name, params, eventId) => {
  // TikTok flags product events without content_id — skip them rather than send them empty.
  if (TT_CONTENT_EVENTS.includes(name) && !(params?.items || []).some((i) => i?.item_id)) return;
  if (!window.ttq) { if (pendingTt.length < 50) pendingTt.push([name, params, eventId]); return; }
  try {
    const args = [TT_EVENTS[name] || name, tiktokParams(params)];
    if (eventId) args.push({ event_id: eventId });
    window.ttq.track(...args);
  } catch (err) { devWarn("[MarketingLoader] ttq failed", name, err); }
};
const flushTikTok = () => { while (window.ttq && pendingTt.length) ttTrack(...pendingTt.shift()); };

const loadSnap = (id) => {
  if (!id) return;
  inlineScript("fl-snap", `
    (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
    a.queue=[];var s='script';r=t.createElement(s);r.async=!0;r.src=n;var u=t.getElementsByTagName(s)[0];
    u.parentNode.insertBefore(r,u);})(window,document,'https://sc-static.net/scevent.min.js');
    snaptr('init', '${id}'); snaptr('track', 'PAGE_VIEW');
  `);
};

const loadGleap = (key, identity) => {
  if (!key) return;
  const install = () => {
    if (!window.Gleap) return setTimeout(install, 200);
    try {
      window.Gleap.initialize(key);
      if (identity && identity.id) {
        // With Gleap identity verification enforced, the server-computed HMAC hash is required.
        window.Gleap.identify(String(identity.id), { name: identity.name, email: identity.email }, identity.hash || undefined);
      }
    } catch (_) {}
  };
  scriptTag("https://sdk.gleap.io/latest/index.js", "fl-gleap");
  install();
};

/**
 * Corridor dimensions (who is sending, where it lands) attached to every event
 * so revenue can be sliced by corridor in GA4.
 */
const CORRIDOR_KEY = "fl_corridor";

export const setCorridor = (patch) => {
  try {
    const current = JSON.parse(localStorage.getItem(CORRIDOR_KEY) || "{}");
    const next = { ...current, ...Object.fromEntries(Object.entries(patch).filter(([, v]) => v)) };
    localStorage.setItem(CORRIDOR_KEY, JSON.stringify(next));
  } catch (_) {}
};

const corridor = () => {
  try { return JSON.parse(localStorage.getItem(CORRIDOR_KEY) || "{}"); } catch (_) { return {}; }
};

const track = (name, incoming = {}, fbEventId = null) => {
  const params = { ...corridor(), ...incoming };
  const fbName = name === "purchase" ? "Purchase" : name === "add_to_cart" ? "AddToCart"
    : name === "begin_checkout" ? "InitiateCheckout" : name === "view_item" ? "ViewContent" : "CustomEvent";
  try { if (window.gtag) window.gtag("event", name, params); } catch (err) { devWarn("[MarketingLoader] gtag failed", name, err); }
  fb("track", fbName, metaParams(params), fbEventId ? { eventID: fbEventId } : undefined);
  ttTrack(name, params, fbEventId);
  try { if (window.snaptr) window.snaptr("track", name === "purchase" ? "PURCHASE" : name === "add_to_cart" ? "ADD_CART" : name === "begin_checkout" ? "START_CHECKOUT" : name === "view_item" ? "VIEW_CONTENT" : "CUSTOM_EVENT", snapParams(params)); } catch (err) { devWarn("[MarketingLoader] snaptr failed", name, err); }
  try { if (window._linkedin_data_partner_ids) window.lintrk?.("track", { conversion_id: name }); } catch (err) { devWarn("[MarketingLoader] lintrk failed", name, err); }
  oaiMeasure(name, params, fbEventId);
};

export const trackPageView = (path) => {
  try { window.gtag?.("event", "page_view", { page_path: path, ...corridor() }); } catch (err) { devWarn("[MarketingLoader] gtag page_view failed", err); }
  fb("track", "PageView");
  try { window.ttq?.page?.(); } catch (err) { devWarn("[MarketingLoader] ttq page failed", err); }
  try { window.snaptr?.("track", "PAGE_VIEW"); } catch (err) { devWarn("[MarketingLoader] snaptr PAGE_VIEW failed", err); }
  try { window.oaiq?.("measure", "page_viewed", { type: "contents", contents: [{ id: path, name: path, content_type: "page" }] }); }
  catch (err) { devWarn("[MarketingLoader] oaiq page_viewed failed", err); }
};

// Signed-in buyer (set by MarketingLoader). Sent raw over HTTPS to our server, which hashes before forwarding.
let signedInBuyer = null;
const signedInIdentity = () => (signedInBuyer
  ? { external_id: signedInBuyer.id, email: signedInBuyer.email || "", phone: signedInBuyer.whatsapp || signedInBuyer.phone || "" }
  : {});

export const trackViewItem = (product) => {
  const eventId = `vc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const currency = product?.currency || "EUR";
  const value = product?.price || product?.price_mad || 0;
  track("view_item", {
    currency, value,
    items: [{ item_id: product?.id, item_name: product?.title, price: value, quantity: 1 }],
  }, eventId);
  // Server copy through the Conversions API (same event id → Meta keeps one). Only with consent,
  // and slightly delayed so the pixel has set the _fbp cookie on a direct landing.
  const href = window.location.href;
  setTimeout(() => {
    const cfg = window.__fleurinelleMarketing || {};
    let consented = false;
    try { consented = localStorage.getItem("fl_cookie_consent") === "accepted"; } catch (_) {}
    const meta = !!cfg.meta_pixel_id && (consented || cfg.meta_pixel_pre_consent === "on");
    const tiktok = !!cfg.tiktok_pixel_id && (consented || cfg.tiktok_pixel_pre_consent === "on");
    if (!meta && !tiktok) return;
    api.post("/meta/events", {
      event_name: "ViewContent", event_id: eventId, event_source_url: href, meta, tiktok,
      ...signedInIdentity(),
      fbp: cookieValue("_fbp"), fbc: cookieValue("_fbc"), ...tiktokIds(),
      items: [{ product_id: String(product?.id || ""), title: product?.title || "", price: value, quantity: 1 }],
      custom_data: { currency, value, content_type: "product", content_ids: [String(product?.id || "")],
                     content_name: product?.title || "" },
    }).catch(() => {});
  }, 1500);
};

export const trackAddToCart = (product) => {
  const eventId = `atc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const currency = product?.currency || "MAD";
  const value = product?.price || product?.price_mad || 0;
  track("add_to_cart", {
    currency, value,
    items: [{ item_id: product?.id, item_name: product?.title,
              price: product?.price || product?.price_mad, quantity: 1 }],
  }, eventId);
  // Server-side signal → immediate CAPI AddToCart + queued for retargeting sweep.
  try {
    api.post("/meta/cart-signal", {
      event_id: eventId, event_source_url: window.location.href,
      email: product?.buyer_email || "", country: product?.buyer_country || "",
      fbp: cookieValue("_fbp"), fbc: cookieValue("_fbc"), ...tiktokIds(),
      currency, value,
      items: [{ product_id: product?.id, title: product?.title,
                price: value, quantity: 1 }],
    }).catch(() => {});
  } catch (_) {}
};

export const trackBeginCheckout = (basket) => {
  const eventId = `checkout_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const value = basket?.total || basket?.total_mad || 0;
  const currency = basket?.currency || "MAD";
  track("begin_checkout", {
    currency,
    value,
    items: (basket?.items || []).map((i) => ({ item_id: i.product_id, item_name: i.title,
                                               price: i.price_mad, quantity: i.quantity || 1 })),
  }, eventId);
  // Same event through the Conversions APIs (Meta + ChatGPT Ads), deduplicated by event id.
  try {
    api.post("/meta/events", {
      event_name: "InitiateCheckout", event_id: eventId, event_source_url: window.location.href,
      email: basket?.buyer_email || "", country: basket?.buyer_country || "",
      city: basket?.recipient_city || "", fbp: cookieValue("_fbp"), fbc: cookieValue("_fbc"), ...tiktokIds(),
      phone: basket?.buyer_phone || "",
      obref: cookieValue("__obref"), oppref: cookieValue("__oppref"),
      items: (basket?.items || []).map((i) => ({ product_id: i.product_id, title: i.title,
                                                 price: i.price_mad, quantity: i.quantity || 1 })),
      custom_data: { currency, value, content_type: "product",
                     content_ids: (basket?.items || []).map((i) => String(i.product_id)).filter(Boolean),
                     num_items: (basket?.items || []).reduce((n, i) => n + (i.quantity || 1), 0) },
    }).catch(() => {});
  } catch (_) {}
};

export const trackPurchase = (order, adsConfig) => {
  const gross = order?.total || order?.total_mad || 0;
  const currency = order?.currency || "MAD";
  // Ad platforms bid on the margin (Admin → Marketing → conversion value); GA4 keeps the real revenue.
  const cv = order?.conversion_value ?? gross;
  const cvCurrency = order?.conversion_currency || currency;
  const items = (order?.items || []).map((i) => ({ item_id: i.product_id, item_name: i.title,
                                                    item_variant: i.variant,
                                                    price: i.price ?? i.price_mad, quantity: i.quantity || 1 }));
  const orderRef = order?.order_number || order?.id;
  const eventId = `purchase_${orderRef}`;
  const base = corridor();
  // GA4 ecommerce — the real sale, so revenue reporting stays accurate.
  try { window.gtag?.("event", "purchase", { ...base, transaction_id: orderRef, value: gross, currency, items }); }
  catch (err) { devWarn("[MarketingLoader] gtag purchase failed", err); }
  // Every ad platform optimises on profit. The server repeats these with the same ids — no double counting.
  const adParams = { ...base, transaction_id: orderRef, value: cv, currency: cvCurrency, items };
  fb("track", "Purchase", metaParams(adParams), { eventID: eventId });
  applyTikTokIdentity();
  ttTrack("purchase", adParams, eventId);
  try { window.snaptr?.("track", "PURCHASE", snapParams(adParams)); } catch (err) { devWarn("[MarketingLoader] snaptr purchase failed", err); }
  oaiMeasure("purchase", adParams, eventId);
  try {
    if (adsConfig?.conversion_id && adsConfig?.label && window.gtag) {
      window.gtag("event", "conversion", {
        send_to: `${adsConfig.conversion_id}/${adsConfig.label}`,
        value: cv, currency: cvCurrency, transaction_id: orderRef,
      });
    }
  } catch (_) {}
};

/** Checkout submitted and order created, right before the payment page (TikTok PlaceAnOrder). */
export const trackPlaceOrder = (basket) => {
  const items = (basket?.items || []).map((i) => ({ item_id: i.product_id, item_name: i.title,
                                                    price: i.price_mad, quantity: i.quantity || 1 }));
  const orderRef = (basket?.order_numbers || [])[0] || Date.now();
  const params = { currency: basket?.currency || "EUR", value: basket?.total || 0, items };
  ttTrack("place_order", params, `order_${orderRef}`);
  // The buyer is sent to the secure payment page next — TikTok's AddPaymentInfo funnel step.
  ttTrack("add_payment_info", params, `payinfo_${orderRef}`);
};

export const trackAddToWishlist = (product) => {
  const params = { currency: product?.currency || "EUR", value: product?.price || 0,
                   items: [{ item_id: product?.id, item_name: product?.title, price: product?.price || 0, quantity: 1 }] };
  try { window.gtag?.("event", "add_to_wishlist", params); } catch (_) {}
  fb("track", "AddToWishlist", metaParams(params));
  ttTrack("add_to_wishlist", params);
};

export const trackSearch = (query, products = [], currency = "EUR") => {
  const items = products.slice(0, 10).map((p) => ({ item_id: p.id, item_name: p.title, price: p.price || 0, quantity: 1 }));
  const params = { currency, value: 0, search_string: query, items };
  try { window.gtag?.("event", "search", { search_term: query }); } catch (_) {}
  fb("track", "Search", { ...metaParams(params), search_string: query });
  ttTrack("search", params);
};

/**
 * Loads every pixel the admin has configured and, on florist routes, the
 * florist Gleap widget; on all other routes, the customer Gleap widget with
 * the buyer's identity when known.
 */
export default function MarketingLoader({ user }) {
  const { pathname } = useLocation();
  const firstPath = useRef(pathname);
  const [cfg, setCfg] = useState(null);
  const [me, setMe] = useState(null);
  const [consent, setConsent] = useState(() => {
    try { return localStorage.getItem("fl_cookie_consent") === "accepted"; } catch (_) { return false; }
  });

  useEffect(() => {
    api.get("/public/site-config").then((r) => setCfg(r.data)).catch(() => {});
    const onConsent = (e) => setConsent(e.detail === "accepted");
    window.addEventListener("fl-cookie-consent", onConsent);
    return () => window.removeEventListener("fl-cookie-consent", onConsent);
  }, []);

  // Fetch the logged-in user (incl. server-computed Gleap identity hash) when a token exists.
  useEffect(() => {
    let token = null;
    try { token = localStorage.getItem("fl_token"); } catch (_) {}
    if (!token) { setMe(null); return; }
    api.get("/auth/me").then((r) => setMe(r.data.user)).catch(() => setMe(null));
  }, [user]);

  useEffect(() => {
    if (!cfg) return;
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const onFloristRoute = path.startsWith("/florist");
    const onAdminRoute = path.startsWith("/admin");
    const m = cfg.marketing || {};
    // Expose the marketing config to same-page callers (partner-signup trackers,
    // etc.) so they can use `AW-XXX/LABEL` conversion IDs configured by admins.
    if (typeof window !== "undefined") window.__fleurinelleMarketing = m;
    // Google tags run on the storefront from the first pageview under Consent Mode v2
    // (cookieless until the banner is accepted). Pixels without a consent API stay gated.
    if (!onFloristRoute && !onAdminRoute) {
      installConsentDefaults(consent);
      loadGoogle(m.ga_measurement_id, m.gtm_container_id, m.google_ads_conversion_id);
      if (consent) updateConsent("accepted");
      metaTag("facebook-domain-verification", m.facebook_domain_verification);
      loadMetaPixel(m.meta_pixel_id, consent, m.meta_pixel_pre_consent === "on");
      updateMetaConsent(consent || m.meta_pixel_pre_consent === "on" ? "accepted" : "rejected");
      loadOpenAiPixel(m.openai_ads_pixel_id, consent);
      if (consent) updateOpenAiConsent("accepted");
    }
    // TikTok fires from the first visit when pre-consent is on (same choice as Meta), otherwise after Accept.
    if (!onFloristRoute && !onAdminRoute && (consent || m.tiktok_pixel_pre_consent === "on")) {
      loadTikTok(m.tiktok_pixel_id);
      if (consent) updateTikTokConsent("accepted");
    }
    if (consent && !onFloristRoute && !onAdminRoute) {
      loadLinkedIn(m.linkedin_partner_id);
      loadSnap(m.snapchat_pixel_id);
    }
    // gleap: florist widget on /florist, no widget on /admin, customer widget elsewhere
    const gleapKey = onAdminRoute ? "" : onFloristRoute ? m.gleap_florists_key : m.gleap_customers_key;
    const u = me || user;
    const identity = u ? { id: u.id, email: u.email, name: u.name || u.email, hash: u.gleap_user_hash } : null;
    loadGleap(gleapKey, identity);
    if (consent && u && !onFloristRoute && !onAdminRoute) identifyBuyer({ email: u.email, phone: u.whatsapp || u.phone, externalId: u.id });
    let impersonating = false;
    try { impersonating = !!localStorage.getItem("fl_impersonating_customer"); } catch (_) {}
    signedInBuyer = u && u.role === "buyer" && u.id && !impersonating ? u : null;
  }, [cfg, user, me, consent]);

  // SPA navigations: the snippet only counts the landing page, so later routes send their own PageView.
  useEffect(() => {
    if (pathname === firstPath.current) return;
    firstPath.current = pathname;
    if (!pathname.startsWith("/admin") && !pathname.startsWith("/florist") && !pathname.startsWith("/driver")) trackPageView(pathname);
  }, [pathname]);

  return null;
}

export { track };
